import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";
import {MenuItem, Stack} from "@mui/material";
import {withStyles} from "@mui/styles";
import debounce from 'lodash.debounce';

import Card from "components/card/card";
import CardBody from "components/card/cardBody";
import CardHeader from "components/card/cardHeader";
import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";
import ListingProductPageHeader from "components/page/ListingProductPageHeader";
import ListingPagination from "components/Product/Table/ListingPagination";
import ListingSortButton from "components/Product/Table/ListingSortButton";
import ListingActionsSelect from "components/Product/Table/ListingActionsSelect";
import {ITEM_PER_PAGE_CHOICES} from "config/_pagination";
import {FilterContext} from "contexts/filterContext";
import {getEnrichmentValues} from "api/enrichment";
import listStyle from "assets/jss/views/product/listStyle";
import Filters from "views/marketplace/merchandising/filter/filters";
import useMerchandisingFilterTabs from "views/marketplace/merchandising/hook/useMerchandisingFilterTabs";

import EnrichmentList from "./EnrichmentList";
import MassActionBar from "./MassActionBar";
import StateSelector from "./StateSelector";
import TargetChips from "./TargetChips";
import TypeTabs from "./TypeTabs";
import {ENRICHMENT_TAB_ORDER, ENRICHMENT_TABS, isAttributeTab, tabCountsOf} from "../enrichmentConstants";
import useEnrichmentCounts from "../hook/useEnrichmentCounts";
import useEnrichmentDecisions from "../hook/useEnrichmentDecisions";
import useEnrichmentList from "../hook/useEnrichmentList";

EnrichmentScreen.propTypes = {
    retailerId: PropTypes.string,
    currentUserName: PropTypes.string,
    classes: PropTypes.object.isRequired,
};

const SORT_OPTIONS = [
    {value: 'opportunity', labelId: 'enrichment.sort.opportunity'},
    {value: 'groupedSearchImpression', labelId: 'enrichment.sort.attractiveness'},
    {value: 'totalSales', labelId: 'enrichment.sort.sales'},
    {value: 'ctr', labelId: 'enrichment.sort.ctr'},
    {value: 'score', labelId: 'enrichment.sort.score'},
    {value: 'translations.name', labelId: 'enrichment.sort.alphabetical'},
];

function GlobalFilters() {
    const {filters, filtersLoaded} = useMerchandisingFilterTabs();

    return <Filters filters={filters} loaded={filtersLoaded} withoutBottomMargin/>;
}

function EnrichmentScreen({retailerId = null, currentUserName = null, classes}) {
    const filterContext = useContext(FilterContext);
    const filterParams = useMemo(() => (null === retailerId ? (filterContext?.filterParams ?? {}) : {}), [retailerId, filterContext?.filterParams]);

    const [state, setState] = useState('missing');
    const [tab, setTab] = useState(ENRICHMENT_TABS.PRIMARY_ATTRIBUTES);
    const [target, setTarget] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [sort, setSort] = useState({property: 'opportunity', order: 'desc'});
    const [values, setValues] = useState({});
    const listRef = useRef(null);

    const debouncedSetSearch = useMemo(() => debounce((value) => {
        setSearch(value);
        setPage(1);
    }, 400), []);
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

    useEffect(() => {
        getEnrichmentValues().then(setValues).catch(() => setValues({}));
    }, []);

    useEffect(() => {
        setPage(1);
    }, [filterParams]);

    const body = useMemo(() => ({
        ...filterParams,
        ...('' !== search ? {searchInput: search} : {}),
    }), [filterParams, search]);

    const {counts, setCounts} = useEnrichmentCounts(retailerId, body);
    const list = useEnrichmentList(retailerId, body, {tab, state, target, search, page, itemsPerPage, sort});
    const decisions = useEnrichmentDecisions({...list, tab, state, counts, setCounts, currentUserName});

    const changeView = (setter) => (value) => {
        list.resetList();
        setter(value);
        setTarget(null);
        setPage(1);
    };

    const changeSort = (property) => {
        setSort((current) => ({
            property,
            order: current.property === property
                ? ('asc' === current.order ? 'desc' : 'asc')
                : ('translations.name' === property ? 'asc' : 'desc'),
        }));
        setPage(1);
    };

    const isAttributes = isAttributeTab(tab);
    const selectedProducts = list.rows.filter((product) => list.selected[product.id]);
    const currentCounts = tabCountsOf(counts, tab);
    const tabCounts = Object.fromEntries(ENRICHMENT_TAB_ORDER.map((orderedTab) => [orderedTab, tabCountsOf(counts, orderedTab)[state]?.products ?? 0]));
    const stateCounts = {
        missing: counts.totals?.missing ?? 0,
        pending: counts.totals?.pending ?? 0,
        applied: counts.totals?.applied ?? 0,
    };

    return (
        <div className={classes.containerWithSidebar}>
            <div className={classes.container}>
                <GridContainer>
                    <GridItem xs={12}>
                        <Card className={classes.card}>
                            <CardHeader color="info">
                                <ListingProductPageHeader
                                    titleId="enrichment.header.title"
                                    subtitleId="enrichment.header.subtitle"
                                    count={<FormattedMessage id="enrichment.header.count" values={{count: list.totalItems}}/>}
                                    searchValue={searchValue}
                                    searchPlaceholderId="enrichment.header.search.placeholder"
                                    onSearchChange={(event) => {
                                        setSearchValue(event.target.value);
                                        debouncedSetSearch(event.target.value);
                                    }}
                                />
                            </CardHeader>
                            <CardBody>
                                <Stack gap="32px">
                                    {null === retailerId && <GlobalFilters/>}
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <StateSelector value={state} counts={stateCounts} onChange={changeView(setState)}/>
                                        <Stack direction="row" alignItems="center" gap="12px">
                                            {isAttributes && (
                                                <ListingSortButton options={SORT_OPTIONS} activeValue={sort.property} onSelect={changeSort}/>
                                            )}
                                            <ListingActionsSelect
                                                value={String(itemsPerPage)}
                                                autoWidth
                                                onChange={(event) => {
                                                    setItemsPerPage(Number(event.target.value));
                                                    setPage(1);
                                                }}
                                            >
                                                {ITEM_PER_PAGE_CHOICES.map((choice) => (
                                                    <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                                                ))}
                                            </ListingActionsSelect>
                                        </Stack>
                                    </Stack>
                                    <TypeTabs value={tab} counts={tabCounts} onChange={changeView(setTab)}/>
                                    <Stack gap="16px" ref={listRef}>
                                        {isAttributes && (
                                            <TargetChips targets={currentCounts[state]?.targets ?? {}} catalog={values} value={target} onChange={(value) => {
                                                setTarget(value);
                                                setPage(1);
                                            }}/>
                                        )}
                                        <EnrichmentList
                                            tab={tab}
                                            state={state}
                                            rows={list.rows}
                                            loading={list.loading}
                                            values={values}
                                            selected={list.selected}
                                            onSelectedChange={list.setSelected}
                                            onValuesChange={list.handleValuesChange}
                                            decisions={decisions}
                                        />
                                    </Stack>
                                    {list.totalItems > itemsPerPage && (
                                        <ListingPagination
                                            large
                                            currentPage={page}
                                            totalPages={Math.ceil(list.totalItems / itemsPerPage)}
                                            onPageChange={setPage}
                                        />
                                    )}
                                    <MassActionBar
                                        titleId="enrichment.selection.title.attributes"
                                        selectedCount={selectedProducts.length}
                                        onCancel={() => list.setSelected({})}
                                        loading={decisions.deciding}
                                        anchorRef={listRef}
                                        actions={[
                                            {labelId: 'enrichment.action.dismissSelection', color: 'lightRed', onClick: () => decisions.dismissSelection(selectedProducts)},
                                            ...('pending' === state ? [{labelId: 'enrichment.action.applySelection', color: 'green', onClick: () => decisions.applySelection(selectedProducts)}] : []),
                                        ]}
                                    />
                                </Stack>
                            </CardBody>
                        </Card>
                    </GridItem>
                </GridContainer>
            </div>
        </div>
    );
}

export default withStyles(listStyle)(EnrichmentScreen);
